import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  getDoc,
  addDoc,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { useTranslation } from "react-i18next";

function SchedulingInbox({ therapistId, onStatusChange }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, "schedulingRequests"),
        where("therapistId", "==", therapistId),
        where("status", "==", "pending")
      );

      const snapshot = await getDocs(q);
      const enriched = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();
          let athleteName = data.athleteId;
          try {
            const athleteDoc = await getDoc(doc(db, "users", data.athleteId));
            if (athleteDoc.exists()) {
              athleteName =
                athleteDoc.data().fullName ||
                athleteDoc.data().name ||
                data.athleteId;
            }
          } catch {}

          return {
            id: docSnap.id,
            ...data,
            athleteName,
          };
        })
      );

      setRequests(enriched);

      if (onStatusChange) {
        const hasPending = enriched.some((r) => r.status === "pending");
        onStatusChange(hasPending ? "pending" : "approved");
      }
    } catch (e) {
      console.error("Failed to fetch requests:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [therapistId]);

  const updateStatus = async (id, newStatus) => {
    try {
      const requestRef = doc(db, "schedulingRequests", id);
      const requestSnap = await getDoc(requestRef);
      const requestData = requestSnap.data();
      const athleteId = requestData.athleteId;

      await updateDoc(requestRef, {
        status: newStatus,
        updatedAt: new Date(),
      });

      await addDoc(collection(db, "users", athleteId, "notifications"), {
        type: "schedule_response",
        requestId: id,
        status: newStatus,
        timestamp: new Date(),
        read: false,
      });

      await fetchRequests();
    } catch (e) {
      console.error("Failed to update status:", e);
    }
  };

  return (
    <div
      className="bg-white p-4 rounded-md shadow"
      dir={isRTL ? "rtl" : "ltr"}
      style={{ textAlign: isRTL ? "right" : "left" }}
    >
      <h3 className="text-lg font-semibold mb-4">{t("scheduling_requests")}</h3>

      {loading ? (
        <p>{t("loading")}...</p>
      ) : requests.length === 0 ? (
        <p className="text-gray-600 text-sm">{t("no_pending_requests")}</p>
      ) : (
        <ul className="space-y-4">
          {requests.map((req) => (
            <li key={req.id} className="p-4 bg-[#f1efe7] rounded shadow-sm">
              <p className="text-sm mb-1">
                <strong>{req.athleteName}</strong> —{" "}
                {new Date(req.requestedTime).toLocaleString()}
              </p>
              {req.note && (
                <p className="text-xs text-gray-600 mb-2">📝 {req.note}</p>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => updateStatus(req.id, "approved")}
                  className="bg-green-600 text-white px-3 py-1 rounded"
                >
                  {t("confirm")}
                </button>
                <button
                  onClick={() => updateStatus(req.id, "rejected")}
                  className="bg-gray-500 text-white px-3 py-1 rounded"
                >
                  {t("reject")}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default SchedulingInbox;
